import { Controller } from "@hotwired/stimulus"

// Focus state survives the Turbo Frame re-render: the controller element lives
// inside the frame, so each submit disconnects this instance and connects a
// fresh one. A module-level variable bridges that gap — the outgoing instance
// records which field/caret had focus, the incoming instance restores it.
let pendingFocus = null

// Field types whose typing is gated by minLength + debounce. Selects and
// checkboxes are excluded so they still submit instantly on change.
const TEXTUAL_TYPES = ["text", "search", "number"]

export default class extends Controller {
  static targets = ["form"]
  static values = {
    debounce: { type: Number, default: 300 },
    minLength: { type: Number, default: 3 }
  }

  connect() {
    this._timeout = null
    this.#restoreFocus()
  }

  disconnect() {
    clearTimeout(this._timeout)
  }

  filter(event) {
    const input = event.target

    if (TEXTUAL_TYPES.includes(input.type)) {
      const value = input.value.trim()
      // Wait for at least minLength characters before querying, but an empty
      // field still submits so clearing a filter takes effect immediately.
      if (value.length > 0 && value.length < this.minLengthValue) return
    }

    clearTimeout(this._timeout)
    this._timeout = setTimeout(() => {
      pendingFocus = this.#captureFocus(input)
      this.formTarget.requestSubmit()
    }, this.debounceValue)
  }

  #captureFocus(input) {
    const state = { name: input.name }
    try {
      state.start = input.selectionStart
      state.end = input.selectionEnd
    } catch {
      // number/select inputs don't expose selection — focus alone is enough.
      state.start = state.end = null
    }
    return state
  }

  #restoreFocus() {
    if (!pendingFocus) return

    const el = this.element.querySelector(`[name="${pendingFocus.name}"]`)
    if (el) {
      el.focus()
      this.#restoreCaret(el)
    }
    pendingFocus = null
  }

  #restoreCaret(el) {
    // Text inputs expose a caret position — restore it exactly.
    if (pendingFocus.start != null) {
      try {
        el.setSelectionRange(pendingFocus.start, pendingFocus.end)
        return
      } catch {
        // fall through to the value-reassign trick below
      }
    }

    // Number inputs don't support setSelectionRange, and focus() alone leaves
    // the caret at the start. Re-assigning the value moves it to the end so
    // typing continues after the last digit.
    if (el.type === "number" && el.value !== "") {
      const value = el.value
      el.value = ""
      el.value = value
    }
  }
}
